/**
 * Simple test page for filter functionality
 * Uses minimal dependencies to ensure it loads
 */

import React, { useState } from 'react';

const FilterTestPage = () => {
  const [filters, setFilters] = useState({
    dateRange: { start: '', end: '' },
    states: [],
    combinedOddsThreshold: ''
  });

  const handleDateChange = (type, value) => {
    setFilters(prev => ({
      ...prev,
      dateRange: { ...prev.dateRange, [type]: value }
    }));
  };

  const handleStateChange = (state, checked) => {
    setFilters(prev => ({
      ...prev,
      states: checked 
        ? [...prev.states, state]
        : prev.states.filter(s => s !== state)
    }));
  };

  const handleOddsChange = (value) => {
    setFilters(prev => ({
      ...prev,
      combinedOddsThreshold: value
    }));
  };

  const states = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">Filter Test Page</h1>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Filter Controls */}
        <div className="bg-white border rounded-lg p-4">
          <h2 className="text-lg font-semibold mb-4">Filters</h2>
          
          {/* Date Range */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Date Range</label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-gray-500 mb-1">From</label>
                <input
                  type="date"
                  value={filters.dateRange.start}
                  onChange={(e) => handleDateChange('start', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">To</label>
                <input
                  type="date"
                  value={filters.dateRange.end}
                  onChange={(e) => handleDateChange('end', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md text-sm"
                />
              </div>
            </div>
          </div>

          {/* States */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">States</label>
            <div className="grid grid-cols-2 gap-2">
              {states.map(state => (
                <label key={state} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={filters.states.includes(state)}
                    onChange={(e) => handleStateChange(state, e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm">{state}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Combined Odds */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Combined Odds Threshold</label>
            <input
              type="number"
              step="0.1"
              placeholder="e.g., 10.0"
              value={filters.combinedOddsThreshold}
              onChange={(e) => handleOddsChange(e.target.value)}
              className="w-full px-3 py-2 border rounded-md text-sm"
            />
          </div>
        </div>

        {/* Filter Display */}
        <div className="bg-white border rounded-lg p-4">
          <h2 className="text-lg font-semibold mb-4">Current Filters</h2>
          
          <div className="space-y-3">
            <div>
              <strong className="text-sm">Date Range:</strong>
              <div className="text-sm text-gray-600">
                {filters.dateRange.start || 'Not set'} to {filters.dateRange.end || 'Not set'}
              </div>
            </div>

            <div>
              <strong className="text-sm">Selected States:</strong>
              <div className="text-sm text-gray-600">
                {filters.states.length > 0 ? filters.states.join(', ') : 'None selected'}
              </div>
            </div>

            <div>
              <strong className="text-sm">Odds Threshold:</strong>
              <div className="text-sm text-gray-600">
                {filters.combinedOddsThreshold || 'Not set'}
              </div>
            </div>
          </div>

          <div className="mt-4 p-3 bg-gray-50 rounded">
            <strong className="text-sm">Filter JSON:</strong>
            <pre className="text-xs mt-2 overflow-auto">
              {JSON.stringify(filters, null, 2)}
            </pre>
          </div>
        </div>
      </div>

      <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
        <h3 className="text-lg font-semibold text-green-800 mb-2">✅ Filter Test Status</h3>
        <p className="text-green-700">
          If you can see this page and interact with the filters above, the basic filtering system is working correctly!
        </p>
        <p className="text-green-700 mt-2">
          This page uses minimal dependencies and demonstrates the core filtering functionality we implemented.
        </p>
      </div>
    </div>
  );
};

export default FilterTestPage;